import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import type { ReactNode } from "react";
import { AuthProvider, useAuth } from "./lib/auth.tsx";
import { Layout } from "./components/Layout.tsx";
import { Spinner } from "./components/ui.tsx";
import { LoginPage } from "./pages/Login.tsx";
import { DashboardPage } from "./pages/Dashboard.tsx";
import { DevicesPage } from "./pages/Devices.tsx";
import { DeviceDetailPage } from "./pages/DeviceDetail.tsx";
import { RequestsPage } from "./pages/Requests.tsx";
import { GroupsPage } from "./pages/Groups.tsx";
import { UsersPage } from "./pages/Users.tsx";
import { RemotePage } from "./pages/Remote.tsx";

const BASENAME = (() => {
  const p = window.location.pathname;
  const known = ["/login", "/dashboard", "/devices", "/requests", "/groups", "/users", "/remote"];
  for (const seg of known) {
    if (p === seg || p.startsWith(seg + "/")) return "";
  }
  // Subdirectory deployment e.g. /Secure_Endpoint/devices
  const first = p.split("/")[1];
  if (first && !known.some((k) => k === "/" + first || k.startsWith("/" + first + "/"))) {
    return "/" + first;
  }
  return "";
})();

function RequireAuth({ children, permission }: { children: ReactNode; permission?: string }) {
  const { user, loading, can } = useAuth();

  if (loading) {
    return (
      <div className="page" style={{ alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
        <Spinner />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (permission && !can(permission)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export function App() {
  return (
    <AuthProvider>
      <BrowserRouter basename={BASENAME}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/remote/:sid"
            element={
              <RequireAuth permission="actions.issue">
                <RemotePage />
              </RequireAuth>
            }
          />
          <Route
            element={
              <RequireAuth>
                <Layout />
              </RequireAuth>
            }
          >
            <Route path="/" element={<DashboardPage />} />
            <Route path="/devices" element={<DevicesPage />} />
            <Route path="/devices/:uid" element={<DeviceDetailPage />} />
            <Route path="/requests" element={<RequestsPage />} />
            <Route
              path="/groups"
              element={
                <RequireAuth permission="groups.read">
                  <GroupsPage />
                </RequireAuth>
              }
            />
            <Route
              path="/users"
              element={
                <RequireAuth permission="users.manage">
                  <UsersPage />
                </RequireAuth>
              }
            />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
